import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	OnChanges,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { TeranetService } from '@services/teranet/teranet.service';
import { Address } from '@models/address';
import { MatterLRO } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import LogRocket from 'logrocket';
import { firstValueFrom, Observable } from 'rxjs';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ICON } from '@core/util/icon';
import { openCloseWithState } from '@core/animations/animations';
import { SnackbarService } from '@services/ui/snackbar.service';
import { TestHooks } from './search-by-address.hooks';
import { PinsResult } from '@models/teranet';

@UntilDestroy()
@Component({
	selector: 'app-teranet-search-by-address',
	templateUrl: './search-by-address.component.html',
	animations: [openCloseWithState()],
	standalone: false,
})
export class SearchByAddressComponent implements OnInit, OnChanges {
	testHooks = TestHooks;
	public streetNumber: string;
	public streetName: string;
	public unitNumber: string;
	public pins: PinsResult;
	public searchingPins: boolean;
	public error;
	public submitted = false;
	public ICON = ICON;
	public showSearchSection = true;

	@Input() isOverview = false;
	@Input() showSearch = true;
	@Output() searchCompletedEvent = new EventEmitter<boolean>();

	@Select(SelectedMatter.address) address$: Observable<Address>;

	@SelectSnapshot(SelectedMatter.LRO) lro: MatterLRO;
	@SelectSnapshot(SelectedMatter.id) saleId: string;
	@SelectSnapshot(SelectedMatter.address) address;
	constructor(
		private teranetService: TeranetService,
		private snackbar: SnackbarService,
	) {}

	ngOnInit(): void {
		this.address$.pipe(untilDestroyed(this)).subscribe((value) => {
			if (
				value?.street &&
				!(this.streetName?.length > 0) &&
				!(this.streetNumber?.length > 0) &&
				!(this.unitNumber?.length > 0)
			) {
				this.setAddress();
			}
		});
	}

	ngOnChanges(): void {
		this.showSearchSection = this.showSearch;
	}

	async searchByAddress(form: NgForm) {
		this.error = null;
		this.submitted = true;

		if (form.valid) {
			this.searchingPins = true;
			try {
				this.pins = await firstValueFrom(
					this.teranetService.searchByAddress({
						lro: this.lro.selected.id,
						street_number: this.streetNumber ? this.streetNumber.trim() : '',
						street_name: this.streetName ? this.streetName.trim() : '',
						unit_number: this.unitNumber ? this.unitNumber.trim() : '',
					}),
				);
				this.searchCompleted(true);
			} catch (err) {
				let friendlyError: string;
				if (!this.lro.selected?.id) {
					this.snackbar.openSnackBar({
						text: 'Error searching for PIN by address - Please Select an LRO',
						type: 'error',
					});
					friendlyError =
						'Error searching for PIN by address - Please Select an LRO';
				} else
					friendlyError = this.teranetService.HandleError(
						'Unable to search PINs',
						err,
					);

				this.error = friendlyError;
				this.searchCompleted(false);
				LogRocket.log('Error searching for pin by address', err);
			} finally {
				this.searchingPins = false;
			}
		} else {
			this.error = 'Unable to search PINs: Invalid search criteria';
		}
	}

	setAddress(): void {
		if (this.address) {
			this.streetNumber = this.address?.street?.match(/\d+/)?.join('') ?? '';
			this.streetName = this.address?.street?.replace(/[0-9]/g, '');
			this.unitNumber = this.address?.unit ?? undefined;
		}
	}

	searchCompleted(value: boolean) {
		this.searchCompletedEvent.emit(value);
	}
}
